import { Button, Modal, useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { errorMessage, setExtensionPreferenceMutation } from "@/api";
import { Text } from "@/components/Text";
import type { ExtensionAccessRequest } from "./chatStreamReducer";

/**
 * The assistant needed an extension permission the user turned off. Allowing it saves the same
 * choice as the switch in Settings → Extensions, then the chat retries the message.
 */
export function ExtensionAccessDialog({
  request,
  onClose,
  onAllowed,
}: Readonly<{ request: ExtensionAccessRequest; onClose: () => void; onAllowed: () => void }>) {
  const { t } = useTranslation("chat");
  const toast = useToast();
  const allow = useMutation(setExtensionPreferenceMutation());
  const permission = t(`extensionAccess.permission.${request.permission}`);

  const submit = () =>
    allow.mutate(
      { path: { slug: request.slug }, body: { permission: request.permission, enabled: true } },
      {
        onSuccess: onAllowed,
        onError: (error) => toast.error(errorMessage(error, t("extensionAccess.failed", { name: request.name }))),
      },
    );

  return (
    <Modal
      open
      onClose={() => !allow.isPending && onClose()}
      title={t("extensionAccess.title", { name: request.name, permission })}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={allow.isPending}>
            {t("extensionAccess.notNow")}
          </Button>
          <Button variant="primary" loading={allow.isPending} onClick={submit}>
            {t("extensionAccess.allow")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Text variant="body-sm" tone="secondary">
          {t("extensionAccess.body", { name: request.name, permission })}
        </Text>
        <Text variant="body-sm" tone="secondary">
          {t(`extensionAccess.note.${request.permission}`)}{" "}
          <Link
            to="/settings/connections"
            className="btn-focus-ring rounded text-content-primary underline underline-offset-2"
          >
            {t("extensionAccess.manage")}
          </Link>
        </Text>
      </div>
    </Modal>
  );
}
